import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, BrainCircuit, GraduationCap, Layers, Sparkles, Users } from "lucide-react";

import { createWebPageJsonLd, seo } from "@/lib/seo";
import * as m from "@/paraglide/messages";

export const Route = createFileRoute("/(landing)/about/")({
	component: AboutPage,
	head: ({ match }) => {
		const title = `About ${m.about_heading()} | StudyOS`;
		const description = m.about_subtitle();

		return seo({
			title,
			description,
			pathname: match.pathname,
			keywords: ["about StudyOS", "AI education", "interactive learning platform"],
			structuredData: createWebPageJsonLd({
				title,
				description,
				pathname: match.pathname,
			}),
		});
	},
});

function FeatureCard({
	icon: Icon,
	title,
	description,
}: {
	icon: typeof BrainCircuit;
	title: string;
	description: string;
}) {
	return (
		<div className="group relative rounded-2xl border border-border bg-background p-6 transition-all duration-300 hover:border-primary/40 hover:shadow-lg hover:-translate-y-0.5">
			<div className="mb-4 inline-flex rounded-xl bg-primary/10 p-3">
				<Icon className="h-6 w-6 text-primary" />
			</div>
			<h3 className="mb-2 text-lg font-semibold text-foreground">{title}</h3>
			<p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
		</div>
	);
}

function AboutPage() {
	const features = [
		{
			icon: BrainCircuit,
			title: m.about_featureAITitle(),
			description: m.about_featureAIDesc(),
		},
		{
			icon: Layers,
			title: m.about_featureInteractiveTitle(),
			description: m.about_featureInteractiveDesc(),
		},
		{
			icon: Users,
			title: m.about_featurePersonalizedTitle(),
			description: m.about_featurePersonalizedDesc(),
		},
		{
			icon: BookOpen,
			title: m.about_featureMultiSubjectTitle(),
			description: m.about_featureMultiSubjectDesc(),
		},
	];

	return (
		<main className="min-h-screen">
			{/* Hero */}
			<section className="relative flex items-center justify-center overflow-hidden px-4 pt-24 pb-16 md:pt-32 md:pb-24">
				<div className="absolute inset-0 overflow-hidden" aria-hidden="true">
					<div className="absolute top-0 left-1/4 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />
					<div className="absolute right-1/4 bottom-0 h-80 w-80 rounded-full bg-accent/60 blur-3xl" />
				</div>

				<div className="relative z-10 mx-auto max-w-3xl text-center">
					<div
						className="mb-6 flex justify-center opacity-0"
						style={{
							animation: "fade-in-up 0.6s ease-out forwards",
						}}
					>
						<div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-accent px-4 py-1.5 text-sm text-accent-foreground">
							<Sparkles className="h-4 w-4 text-primary" />
							<span>{m.about_badge()}</span>
						</div>
					</div>

					<h1
						className="mb-6 text-4xl font-bold tracking-tight text-foreground opacity-0 md:text-5xl lg:text-6xl"
						style={{
							animation: "fade-in-up 0.6s ease-out 0.1s forwards",
						}}
					>
						{m.about_heading()}
					</h1>

					<p
						className="mx-auto max-w-2xl text-lg leading-relaxed text-muted-foreground opacity-0 md:text-xl"
						style={{
							animation: "fade-in-up 0.6s ease-out 0.2s forwards",
						}}
					>
						{m.about_subtitle()}
					</p>
				</div>
			</section>

			{/* Mission */}
			<section className="px-4 py-16 md:py-24">
				<div className="mx-auto max-w-3xl">
					<div
						className="opacity-0"
						style={{
							animation: "fade-in-up 0.6s ease-out 0.3s forwards",
						}}
					>
						<div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border/60 bg-accent px-3 py-1 text-sm text-accent-foreground">
							<GraduationCap className="h-4 w-4 text-primary" />
							<span>{m.about_missionTitle()}</span>
						</div>
						<p className="text-lg leading-relaxed text-muted-foreground">
							{m.about_missionDesc()}
						</p>
					</div>
				</div>
			</section>

			{/* Features */}
			<section className="px-4 py-16 md:py-24">
				<div className="mx-auto max-w-5xl">
					<div
						className="grid gap-6 opacity-0 sm:grid-cols-2"
						style={{
							animation: "fade-in-up 0.6s ease-out 0.4s forwards",
						}}
					>
						{features.map((feature) => (
							<FeatureCard key={feature.title} {...feature} />
						))}
					</div>
				</div>
			</section>

			{/* Vision */}
			<section className="px-4 py-16 md:py-24">
				<div className="mx-auto max-w-3xl text-center">
					<div
						className="opacity-0"
						style={{
							animation: "fade-in-up 0.6s ease-out 0.5s forwards",
						}}
					>
						<h2 className="mb-4 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
							{m.about_visionTitle()}
						</h2>
						<p className="text-lg leading-relaxed text-muted-foreground">
							{m.about_visionDesc()}
						</p>
					</div>
				</div>
			</section>

			{/* CTA */}
			<section className="px-4 py-16 md:py-24">
				<div className="mx-auto max-w-2xl text-center">
					<div
						className="opacity-0"
						style={{
							animation: "fade-in-up 0.6s ease-out 0.6s forwards",
						}}
					>
						<p className="mb-6 text-muted-foreground">{m.about_ctaDesc()}</p>
						<Link
							to="/"
							className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3 font-medium text-primary-foreground transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
						>
							{m.about_ctaText()}
						</Link>
					</div>
				</div>
			</section>
		</main>
	);
}
